import { SearchOutlined } from '@ant-design/icons';
import { Button, Input, Space, Spin, Table, Tag, Typography } from 'antd';
import nhsvOrderHistoryApiCall from 'apps/nhsv/apiCalls/nhsv/order-history';
import DatePicker from 'common/ui/UIDatePicker';
import { useFetchApiGet } from 'hooks/api/useFetchApi';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const DATE_FORMAT = 'YYYYMMDD';

const formatNumber = (value: string | undefined) => {
  if (!value) return '-';
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num.toLocaleString('vi-VN');
};

const getOrderStatusTag = (status: string) => {
  const map: Record<string, { color: string; label: string }> = {
    '0': { color: 'default', label: 'NHSV Received' },
    '1': { color: 'processing', label: 'Sent to VSD' },
    '2': { color: 'processing', label: 'VSD Received' },
    '3': { color: 'warning', label: 'VSD Approved' },
    '4': { color: 'success', label: 'Matched' },
    '5': { color: 'success', label: 'Partial Match' },
    R: { color: 'error', label: 'NHSV Rejected' },
    X: { color: 'error', label: 'VSD Rejected' },
  };
  const info = map[status];
  if (!info) return <Tag>{status}</Tag>;
  return <Tag color={info.color}>{info.label}</Tag>;
};

const getSellBuyTag = (type: string) => {
  if (type === '1') return <Tag color="red">Sell</Tag>;
  if (type === '2') return <Tag color="green">Buy</Tag>;
  return <Tag>{type}</Tag>;
};

const columns = [
  {
    title: 'Order Date', dataIndex: 'ord_dt', key: 'ord_dt', width: 100,
    render: (value: string) => {
      if (!value) return '-';
      const m = moment(value, 'YYYYMMDD');
      return m.isValid() ? m.format('DD/MM/YYYY') : value;
    },
  },
  { title: 'Order Time', dataIndex: 'ord_tm', key: 'ord_tm', width: 90 },
  { title: 'Order No', dataIndex: 'ord_no', key: 'ord_no', width: 80 },
  { title: 'Stock Code', dataIndex: 'stk_cd', key: 'stk_cd', width: 100 },
  { title: 'Side', dataIndex: 'sellbuy_tp', key: 'sellbuy_tp', width: 70, render: getSellBuyTag },
  {
    title: 'Order Type', dataIndex: 'ord_tp', key: 'ord_tp', width: 80,
    render: (value: string) => {
      const map: Record<string, string> = { '01': 'LO', '03': 'ATO', '04': 'ATC', '09': 'MP' };
      return map[value] || value;
    },
  },
  { title: 'Order Qty', dataIndex: 'ord_qty', key: 'ord_qty', render: formatNumber, align: 'right' as const, width: 100 },
  { title: 'Order Price', dataIndex: 'ord_pri', key: 'ord_pri', render: formatNumber, align: 'right' as const, width: 100 },
  { title: 'Match Qty', dataIndex: 'mth_qty', key: 'mth_qty', render: formatNumber, align: 'right' as const, width: 100 },
  { title: 'Match Price', dataIndex: 'mth_pri', key: 'mth_pri', render: formatNumber, align: 'right' as const, width: 100 },
  { title: 'Unmatch Qty', dataIndex: 'unmth_qty', key: 'unmth_qty', render: formatNumber, align: 'right' as const, width: 100 },
  { title: 'Status', dataIndex: 'ord_stat', key: 'ord_stat', width: 120, render: getOrderStatusTag },
  {
    title: 'Modify/Cancel', dataIndex: 'modcan_tp', key: 'modcan_tp', width: 110,
    render: (value: string) => {
      if (!value || value === '0') return '-';
      if (value === '2') return <Tag color="orange">Modified</Tag>;
      if (value === '4') return <Tag color="red">Cancelled</Tag>;
      return value;
    },
  },
  { title: 'Original Order', dataIndex: 'orgord_no', key: 'orgord_no', width: 110, render: (value: string) => value || '-' },
];

export default function NHSVOrderHistoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlAccountNumber = searchParams.get('account_number') || '';
  const urlFromDate = searchParams.get('from_date') || '';
  const urlToDate = searchParams.get('to_date') || '';

  const [inputValue, setInputValue] = useState(urlAccountNumber);
  const [dateRange, setDateRange] = useState<[any, any]>([
    urlFromDate ? moment(urlFromDate, DATE_FORMAT) : moment().subtract(7, 'days'),
    urlToDate ? moment(urlToDate, DATE_FORMAT) : moment(),
  ]);
  const [allOrders, setAllOrders] = useState<any[]>([]);
  const [nextDate, setNextDate] = useState('');
  const [nextKey, setNextKey] = useState('');
  const [hasSearched, setHasSearched] = useState(!!urlAccountNumber);

  const { data, load, isLoading } = useFetchApiGet(nhsvOrderHistoryApiCall, {
    resourceName: 'data',
  });

  useEffect(() => {
    if (urlAccountNumber) {
      const fromDate = urlFromDate || moment().subtract(7, 'days').format(DATE_FORMAT);
      const toDate = urlToDate || moment().format(DATE_FORMAT);
      load({ account_number: urlAccountNumber, from_date: fromDate, to_date: toDate });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateSearchParams = (acctNum: string, from: string, to: string) => {
    setSearchParams({ account_number: acctNum, from_date: from, to_date: to });
  };

  const handleSearch = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const fromDate = dateRange[0].format(DATE_FORMAT);
    const toDate = dateRange[1].format(DATE_FORMAT);
    setInputValue(trimmed);
    setAllOrders([]);
    setNextDate('');
    setNextKey('');
    setHasSearched(true);
    updateSearchParams(trimmed, fromDate, toDate);
    load({ account_number: trimmed, from_date: fromDate, to_date: toDate });
  };

  const accountNumber = searchParams.get('account_number') || '';

  const handleLoadMore = () => {
    if (!nextDate || !nextKey || !accountNumber) return;
    load({
      account_number: accountNumber,
      from_date: dateRange[0].format(DATE_FORMAT),
      to_date: dateRange[1].format(DATE_FORMAT),
      next_date: nextDate,
      next_key: nextKey,
    });
  };

  const items = data?.items || [];

  const currentOrders = (() => {
    if (!data) return allOrders;
    const newItems = items.filter(
      (item: any) => !allOrders.some(
        existing => existing.ord_no === item.ord_no && existing.ord_dt === item.ord_dt
      )
    );
    if (newItems.length > 0) {
      const merged = [...allOrders, ...newItems];
      if (merged.length !== allOrders.length) {
        setTimeout(() => {
          setAllOrders(merged);
          setNextDate(data.next_date || '');
          setNextKey(data.next_key || '');
        }, 0);
      }
      return merged;
    }
    return allOrders;
  })();

  const hasMore = !!(data?.next_date && data?.next_key);

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>NHSV Order History</Title>

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space wrap>
          <Input
            placeholder="Enter trading account number"
            size="large"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onPressEnter={() => handleSearch(inputValue)}
            style={{ width: 300 }}
            allowClear
          />
          <RangePicker
            size="large"
            value={dateRange}
            onChange={(dates: any) => {
              if (dates && dates[0] && dates[1]) setDateRange([dates[0], dates[1]]);
            }}
            format="DD/MM/YYYY"
          />
          <Button
            type="primary"
            size="large"
            icon={<SearchOutlined />}
            onClick={() => handleSearch(inputValue)}
            loading={isLoading}
          >
            Lookup
          </Button>
        </Space>

        {isLoading && allOrders.length === 0 && (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        )}

        {hasSearched && (
          <>
            <Table
              dataSource={currentOrders}
              columns={columns}
              rowKey={(record: any) => `${record.ord_no}_${record.ord_dt}_${record.ord_tm}`}
              pagination={false}
              size="small"
              scroll={{ x: 1400 }}
              locale={{ emptyText: 'No orders found' }}
              loading={isLoading}
            />
            {hasMore && (
              <div style={{ textAlign: 'center' }}>
                <Button onClick={handleLoadMore} loading={isLoading}>Load More</Button>
              </div>
            )}
          </>
        )}
      </Space>
    </div>
  );
}
